import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
  TableCell,
  TableRow,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  Snackbar,
  Alert,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import Chart from "chart.js/auto";
import {
  FleetFormData,
  FleetReportData,
  fmtMoney,
  fmtNum,
  calculateFleetReport,
  KG_CO2_PER_GAL_GAS,
} from "./calculateFleetReport";
import { ArrowBack, GetApp } from "@mui/icons-material";

import {
  downloadFleetReport,
  FleetReportDetails,
} from "../../utils/downloadFleetReport";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import { getMyAddWhiteLabel } from "../../features/add-white-label-module/addWhiteLabelAction";
import {
  FleetEnergyCosts,
  updateFleetReportData,
} from "../../features/fleet-report-module/fleetReportAction";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(2),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "35px",
  fontWeight: 700,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: "linear-gradient(to left, #f4efe6, #4b5b5d)",
  border: "1px solid #e8ecf7",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(3),
  alignItems: "center",
  boxShadow: "0 8px 26px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const HeroLeft = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
}));

const HeroLabel = styled(Typography)(({ theme }) => ({
  color: "#e8e5dc",
  fontWeight: 600,
  letterSpacing: "0.02em",
  fontSize: "0.9rem",
}));

const HeroValue = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 900,
  lineHeight: 1.1,
  "&.positive": {
    color: "#16a34a",
  },
  "&.negative": {
    color: "#b91c1c",
  },
}));

const HeroNote = styled(Typography)(({ theme }) => ({
  color: "#e8e5dc",
  fontSize: "0.9rem",
}));

const HeroRight = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));

const MiniCard = styled(Box)(({ theme }) => ({
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "12px",
  padding: theme.spacing(2),
  textAlign: "center",
}));

const MiniLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#6b7280",
  marginBottom: theme.spacing(0.5),
}));

const MiniValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 800,
  color: "#4b5b5d",
  "&.positive": {
    color: "#16a34a",
  },
  "&.negative": {
    color: "#b91c1c",
  },
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: theme.spacing(3),
  border: "1px solid #eef2f7",
  boxShadow: "0 6px 20px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 700,
  color: "#182444",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
  },
}));

const KpiGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const KpiCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "14px",
  padding: theme.spacing(2.5),
  border: "1px solid #eef2f7",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  position: "relative",
}));

const KpiLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: "#6b7280",
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

const KpiValue = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 800,
  color: "#182444",
  "&.positive": {
    color: "#16a34a",
  },
  "&.negative": {
    color: "#b91c1c",
  },
}));

const KpiSmall = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#6b7280",
  marginTop: theme.spacing(0.5),
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: "400px",
  width: "100%",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: "#ffffff",
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    height: "350px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "300px",
    padding: theme.spacing(1.5),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(to right, #4b5b5d, #758d8e)",
  color: "#f2efe9",
  fontWeight: 500,
  padding: theme.spacing(1, 3),
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "0.9rem",
  "&:hover": {
    background: "linear-gradient(to right, #758d8e, #4b5b5d)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(0.8, 2),
  },
}));

const ChartsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(3),
}));

const Charts2Col = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const SmallText = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: "#6b7280",
  marginTop: theme.spacing(1),
}));

const MapContainer = styled(Box)(({ theme }) => ({
  height: "300px",
  width: "100%",
  border: "1px solid #ccc",
  borderRadius: "5px",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    height: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "200px",
  },
}));

const TaxBenefitsCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: theme.spacing(3),
  border: "1px solid #eef2f7",
  boxShadow: "0 6px 20px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "1rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
    padding: theme.spacing(0.5),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const StyledTableDataCell = styled(TableCell)(({ theme }) => ({
  color: "#757575",
  fontSize: "0.9rem",
  fontWeight: 500,
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.5),
  },
}));

// Editable KPI Card Component
interface EditableKpiCardProps {
  label: string;
  value: number;
  smallText?: string;
  isEditing: boolean;
  onValueChange: (newValue: number) => void;
  valueType?: "currency" | "number" | "percentage" | "text";
  className?: string;
  readOnly?: boolean;
}

const EditableKpiCard: React.FC<EditableKpiCardProps> = ({
  label,
  value,
  smallText,
  isEditing,
  onValueChange,
  valueType = "number",
  className = "",
  readOnly = false,
}) => {
  const [localValue, setLocalValue] = useState(String(value));

  const handleBlur = () => {
    if (readOnly) return;

    let parsedValue: number;

    if (valueType === "currency") {
      const cleaned = localValue.replace(/[^0-9.-]/g, "");
      parsedValue = cleaned ? Number(cleaned) : 0;
    } else if (valueType === "percentage") {
      const cleaned = localValue.replace(/[^0-9.-]/g, "");
      parsedValue = cleaned ? Number(cleaned) : 0;
    } else {
      const cleaned = localValue.replace(/[^0-9.-]/g, "");
      parsedValue = cleaned ? Number(cleaned) : 0;
    }

    onValueChange(parsedValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (readOnly) return;
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  // Format display value based on type
  const displayValue =
    isEditing && !readOnly
      ? localValue
      : valueType === "currency"
      ? fmtMoney(value)
      : valueType === "percentage"
      ? `${value}%`
      : fmtNum(value, 0);

  return (
    <KpiCard>
      <KpiLabel>{label}</KpiLabel>
      {isEditing && !readOnly ? (
        <TextField
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            "& .MuiInputBase-input": {
              fontSize: "1.5rem",
              fontWeight: 800,
              color: className.includes("positive")
                ? "#16a34a"
                : className.includes("negative")
                ? "#b91c1c"
                : "#182444",
            },
          }}
        />
      ) : (
        <KpiValue className={className}>{displayValue}</KpiValue>
      )}
      {smallText && <KpiSmall>{smallText}</KpiSmall>}
    </KpiCard>
  );
};

export interface ViewFleetReportState {
  formData: FleetFormData;
  reportData: FleetReportData;
  savedReport?: any;
  utilityDetails?: {
    state: string;
    zipCode: string;
    utilityName: string;
    utilityId: string;
    scheduleId: string;
    totalCost: number;
  };
  energyCosts?: {
    energy: number[];
    energyTime: number[];
    incrementalEnergy: number[];
    demand: number[];
    demandTime: number[];
    incrementalDemand: number[];
    serviceCharge: number[];
    otherCharges: number[];
    total: number[];
  };
  disableUtilityEnergyInfo?: boolean;
}

export interface ViewFleetReportProps {
  state?: ViewFleetReportState;
  onBack?: () => void;
}

// Helper functions to extract values from tables
const extractValueFromFinancialTable = (
  table: any[] | undefined,
  category: string
): number => {
  if (!table) return 0;
  const item = table.find((item) => item.category === category);
  if (!item) return 0;
  const value =
    typeof item.value === "string"
      ? item.value.replace(/[^0-9.-]+/g, "")
      : item.value;
  return parseFloat(value.toString()) || 0;
};

const extractValueFromTaxTable = (
  table: any[] | undefined,
  category: string
): number => {
  if (!table) return 0;
  const item = table.find((item) => item.category === category);
  if (!item) return 0;
  const value =
    typeof item.value === "string"
      ? item.value.replace(/[^0-9.-]+/g, "")
      : item.value;
  return parseFloat(value.toString()) || 0;
};

const extractValueFromEnvironmentalTable = (
  table: any[] | undefined,
  category: string
): number => {
  if (!table) return 0;
  const item = table.find((item) => item.category.includes(category));
  if (!item) return 0;
  const value =
    typeof item.value === "string"
      ? item.value.replace(/[^0-9.-]+/g, "")
      : item.value;
  return parseFloat(value.toString()) || 0;
};

const ViewFleetReport: React.FC<ViewFleetReportProps> = ({
  state: propsState,
  onBack,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { whiteLabel, loading: whiteLabelLoading } = useSelector(
    (state: RootState) => state.addWhiteLabel
  );

  const incomingState =
    propsState || (location.state as ViewFleetReportState) || {};
  const savedReport = (incomingState as any)?.savedReport as
    | {
        _id?: string;
        reportName: string;
        location?: string;
        city?: string;
        zipCode?: string;
        fleetSize: number;
        netSavings: number;
        utilityRate: number;
        createdAt?: string;
        financialTable?: Array<{ category: string; value: string | number }>;
        taxBenefitsTable?: Array<{ category: string; value: string | number }>;
        environmentalTable?: Array<{ category: string; value: string }>;
        costOverTimeData?: {
          labels: string[];
          datasets: Array<{ label: string; data: number[] }>;
        };
        cumulativeSavingsData?: {
          labels: string[];
          data: number[];
        };
        environmentalData?: {
          labels: string[];
          data: number[];
        };
        utilityDetails?: {
          state: string;
          zipCode: string;
          utilityName: string;
          utilityId: string;
          scheduleId: string;
          totalCost: number;
        };
        energyCosts?: {
          energy: number[];
          energyTime: number[];
          incrementalEnergy: number[];
          demand: number[];
          demandTime: number[];
          incrementalDemand: number[];
          serviceCharge: number[];
          otherCharges: number[];
          total: number[];
        };
        disableUtilityEnergyInfo?: boolean;
        analysisYears?: number;
        applyTaxBenefits?: boolean;
        paybackPeriod?: number;
        timestamp?: string;
      }
    | undefined;

  const { formData } = incomingState;
  const viewingSavedReportOnly = !!savedReport && !incomingState.formData;

  // Use memoized calculation
  const reportData = useMemo<FleetReportData | undefined>(() => {
    if (savedReport) {
      return {
        address: savedReport.location || "",
        city: savedReport.city || "",
        zipCode: savedReport.zipCode || "",
        utilityRate: savedReport.utilityRate || 0.14,

        // Financial calculations
        iceFuelCostAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual ICE Fuel Cost"
          ) || 0,
        evEnergyCostAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual EV Energy Cost"
          ) || 0,
        iceMaintAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual ICE Maintenance"
          ) || 0,
        evMaintAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual EV Maintenance"
          ) || 0,
        iceOpexAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual ICE Opex"
          ) || 0,
        evOpexAnnual:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual EV Opex"
          ) || 0,
        annualSavings:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Annual Savings"
          ) || 0,

        // Upfront costs
        upfrontEVs:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Upfront EVs Cost"
          ) || 0,
        incentives:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Incentives Total"
          ) || 0,
        resaleTotal:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Resale Value Total"
          ) || 0,
        upfrontGross:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Upfront Gross"
          ) || 0,
        upfrontAfterTax:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Upfront Net Outlay"
          ) || 0,
        upfrontNet:
          extractValueFromFinancialTable(
            savedReport.financialTable,
            "Upfront Net Outlay"
          ) || 0,

        // Tax calculations
        applyTaxBenefits: savedReport.applyTaxBenefits || false,
        businessTaxRate:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Business Tax Rate"
          ) || 21,
        section179Limit:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Section 179 Limit"
          ) || 2500000,
        bonusDepreciationPct:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Bonus Depreciation %"
          ) || 0,
        qualifyingSpend:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Qualifying Spend"
          ) || 0,
        est179Deduction:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Est. Section 179 Deduction"
          ) || 0,
        est168kDeduction:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Est. 168(k) Deduction"
          ) || 0,
        estTaxSavings:
          extractValueFromTaxTable(
            savedReport.taxBenefitsTable,
            "Estimated Tax Savings"
          ) || 0,

        // Long-term results
        paybackYears: savedReport.paybackPeriod || 0,
        totalSavings: savedReport.netSavings || 0,
        netSavings: savedReport.netSavings || 0,

        // Environmental
        gallonsAvoidedAnnual:
          extractValueFromEnvironmentalTable(
            savedReport.environmentalTable,
            "Annual Gasoline Avoided"
          ) || 0,
        co2AnnualKgTailpipe:
          extractValueFromEnvironmentalTable(
            savedReport.environmentalTable,
            "Annual CO2 Offset (Tailpipe)"
          ) || 0,
        gridEmissionsAnnual:
          extractValueFromEnvironmentalTable(
            savedReport.environmentalTable,
            "Annual Grid Emissions"
          ) || 0,
        co2AnnualKgNet:
          extractValueFromEnvironmentalTable(
            savedReport.environmentalTable,
            "Annual Net CO2"
          ) || 0,
        co2NetTons:
          extractValueFromEnvironmentalTable(
            savedReport.environmentalTable,
            `${savedReport.analysisYears || 7}-Year Net CO2`
          ) || 0,

        // Chart data
        costOverTimeIce:
          savedReport?.costOverTimeData?.datasets
            ?.find((d) => d.label === "ICE Opex")
            ?.data.slice(1) || Array(savedReport?.analysisYears || 7).fill(0),
        costOverTimeEv:
          savedReport?.costOverTimeData?.datasets
            ?.find((d) => d.label === "EV Opex")
            ?.data.slice(1) || Array(savedReport?.analysisYears || 7).fill(0),
        cumulativeNetSavings:
          savedReport?.cumulativeSavingsData?.data ||
          Array((savedReport?.analysisYears || 7) + 1).fill(0),
        yearsArray: [
          0,
          ...Array(savedReport?.analysisYears || 7)
            .fill(0)
            .map((_, i) => i + 1),
        ],
      };
    }

    if (!formData) return undefined;
    return calculateFleetReport(
      formData,
      formData.elecPrice ? parseFloat(formData.elecPrice) : 0.14
    );
  }, [formData, savedReport]);

  // Refs and state
  const costOverTimeChartRef = useRef<HTMLCanvasElement>(null);
  const cumulativeChartRef = useRef<HTMLCanvasElement>(null);
  const environmentalChartRef = useRef<HTMLCanvasElement>(null);
  const costComparisonChartRef = useRef<HTMLCanvasElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const costOverTimeChartInstance = useRef<Chart | null>(null);
  const cumulativeChartInstance = useRef<Chart | null>(null);
  const environmentalChartInstance = useRef<Chart | null>(null);
  const costComparisonChartInstance = useRef<Chart | null>(null);

  const [downloading, setDownloading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapView, setMapView] = useState<
    "streetview" | "roadmap" | "satellite"
  >("streetview");
  const [streetViewAvailable, setStreetViewAvailable] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "info" | "warning" | "error" | "success"
  >("info");

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const currentPlanName = (
    userData?.subscription?.planName || ""
  ).toLowerCase();
  const isStarterPlan = currentPlanName === "core";
  const [downloadFormat, setDownloadFormat] = useState<"PDF" | "WL">("PDF");

  // Edit state management
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<{
    financial: { [key: string]: number };
    environmental: { [key: string]: number };
  }>({
    financial: {},
    environmental: {},
  });
  // Add these states to your existing ViewFleetReport component
  const [saving, setSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalculatedNetSavings, setRecalculatedNetSavings] = useState<
    number | null
  >(null);
  const [recalculatedValues, setRecalculatedValues] = useState<{
    iceFuelCostAnnual?: number;
    evEnergyCostAnnual?: number;
    iceMaintAnnual?: number;
    evMaintAnnual?: number;
    iceOpexAnnual?: number;
    evOpexAnnual?: number;
    annualSavings?: number;
    upfrontNet?: number;
    netSavings?: number;
    gallonsAvoidedAnnual?: number;
    co2AnnualKgTailpipe?: number;
    co2AnnualKgNet?: number;
    co2NetTons?: number;
    paybackYears?: number;
  }>({});
  const [recalculationTimeout, setRecalculationTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const defaultUtilityDetails = {
    state: "--",
    zipCode: "--",
    utilityName: "--",
    utilityId: "--",
    scheduleId: "--",
    totalCost: 0,
  };

  const utilityDetails =
    savedReport?.utilityDetails ||
    (!viewingSavedReportOnly &&
      (incomingState.utilityDetails || defaultUtilityDetails)) ||
    undefined;

  const defaultEnergyCosts = {
    energy: Array(5).fill(0),
    energyTime: Array(5).fill(0),
    incrementalEnergy: Array(5).fill(0),
    demand: Array(5).fill(0),
    demandTime: Array(5).fill(0),
    incrementalDemand: Array(5).fill(0),
    serviceCharge: Array(5).fill(0),
    otherCharges: Array(5).fill(0),
    total: Array(5).fill(0),
  };

  const energyCostsView =
    savedReport?.energyCosts ||
    (!viewingSavedReportOnly && incomingState.energyCosts) ||
    (!viewingSavedReportOnly ? defaultEnergyCosts : undefined);
  const hasUtilityDetails = Boolean(utilityDetails);
  const hasEnergyCosts = Boolean(energyCostsView);

  const shouldShowUtilitySection =
    !(
      savedReport?.disableUtilityEnergyInfo ??
      incomingState.disableUtilityEnergyInfo ??
      false
    ) &&
    (hasUtilityDetails || hasEnergyCosts);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const hasWLDownload = downloadFormat === "WL";
    if (hasWLDownload && !whiteLabel && !whiteLabelLoading) {
      dispatch(getMyAddWhiteLabel());
    }
  }, [dispatch, downloadFormat, whiteLabel, whiteLabelLoading]);

  const startEditing = () => {
    const financialValues: { [key: string]: number } = {
      iceFuelCostAnnual: reportData!.iceFuelCostAnnual,
      evEnergyCostAnnual: reportData!.evEnergyCostAnnual,
      iceMaintAnnual: reportData!.iceMaintAnnual,
      evMaintAnnual: reportData!.evMaintAnnual,
      iceOpexAnnual: reportData!.iceOpexAnnual,
      evOpexAnnual: reportData!.evOpexAnnual,
      annualSavings: reportData!.annualSavings,
      upfrontNet: reportData!.upfrontNet,
      netSavings: reportData!.netSavings,
    };

    const environmentalValues: { [key: string]: number } = {
      gallonsAvoidedAnnual: reportData!.gallonsAvoidedAnnual,
      co2AnnualKgTailpipe: reportData!.co2AnnualKgTailpipe,
      co2AnnualKgNet: reportData!.co2AnnualKgNet,
      co2NetTons: reportData!.co2NetTons,
    };

    setEditedValues({
      financial: financialValues,
      environmental: environmentalValues,
    });
    setIsEditing(true);
  };

  const triggerRecalculation = (
    updatedFinancial: { [key: string]: number },
    updatedEnvironmental: { [key: string]: number }
  ) => {
    if (recalculationTimeout) {
      clearTimeout(recalculationTimeout);
    }

    setIsRecalculating(true);

    const timeout = setTimeout(() => {
      recalculateReport(updatedFinancial, updatedEnvironmental);
    }, 500);

    setRecalculationTimeout(timeout);
  };

  const recalculateReport = (
    financial: { [key: string]: number },
    environmental: { [key: string]: number }
  ) => {
    // Extract base values from edited data
    const iceFuelCostAnnual =
      financial.iceFuelCostAnnual || reportData!.iceFuelCostAnnual;
    const evEnergyCostAnnual =
      financial.evEnergyCostAnnual || reportData!.evEnergyCostAnnual;
    const iceMaintAnnual =
      financial.iceMaintAnnual || reportData!.iceMaintAnnual;
    const evMaintAnnual = financial.evMaintAnnual || reportData!.evMaintAnnual;

    // Recalculate derived financial values
    const iceOpexAnnual = iceFuelCostAnnual + iceMaintAnnual;
    const evOpexAnnual = evEnergyCostAnnual + evMaintAnnual;
    const annualSavings = iceOpexAnnual - evOpexAnnual;

    // Use original upfront net (or allow editing if needed)
    const upfrontNet = financial.upfrontNet || reportData!.upfrontNet;

    // Calculate payback period
    const paybackYears =
      annualSavings > 0 ? upfrontNet / annualSavings : Infinity;

    // Calculate net savings over analysis period
    const analysisYears =
      savedReport?.analysisYears || parseInt(formData?.years || "7");
    const totalSavings = annualSavings * analysisYears;
    const netSavings = totalSavings - upfrontNet;

    // ENVIRONMENTAL CALCULATIONS - FIXED VERSION
    // Use edited values or fall back to original values
    let gallonsAvoidedAnnual = environmental.gallonsAvoidedAnnual;
    let co2AnnualKgTailpipe = environmental.co2AnnualKgTailpipe;
    let co2AnnualKgNet = environmental.co2AnnualKgNet;
    let co2NetTons = environmental.co2NetTons;

    // If gallons is edited, recalculate dependent values
    if (environmental.gallonsAvoidedAnnual !== undefined) {
      gallonsAvoidedAnnual = environmental.gallonsAvoidedAnnual;
      // Recalculate CO2 tailpipe based on gallons
      co2AnnualKgTailpipe = Math.round(
        gallonsAvoidedAnnual * KG_CO2_PER_GAL_GAS
      );
    } else {
      gallonsAvoidedAnnual = reportData!.gallonsAvoidedAnnual;
      co2AnnualKgTailpipe = reportData!.co2AnnualKgTailpipe;
    }

    // Get grid emissions from original data (this should remain constant unless edited)
    const gridEmissionsAnnual = reportData!.gridEmissionsAnnual;
    const includeGridEmissions = formData?.includeGridEmissions || false;

    // Calculate net CO2 based on current values
    if (includeGridEmissions) {
      co2AnnualKgNet = Math.round(co2AnnualKgTailpipe - gridEmissionsAnnual);
    } else {
      co2AnnualKgNet = co2AnnualKgTailpipe;
    }

    // Calculate total CO2 tons over analysis period
    co2NetTons = parseFloat(
      ((co2AnnualKgNet * analysisYears) / 1000).toFixed(2)
    );

    // Update state with recalculated values
    setIsRecalculating(false);
    setRecalculatedNetSavings(netSavings);
    setRecalculatedValues({
      iceFuelCostAnnual,
      evEnergyCostAnnual,
      iceMaintAnnual,
      evMaintAnnual,
      iceOpexAnnual,
      evOpexAnnual,
      annualSavings,
      upfrontNet,
      netSavings,
      paybackYears,
      gallonsAvoidedAnnual,
      co2AnnualKgTailpipe,
      co2AnnualKgNet,
      co2NetTons,
    });
  };

  const handleSave = async () => {
    if (!savedReport) return;

    try {
      setSaving(true);
      const updatedFinancialTable = [
        {
          category: "Annual ICE Fuel Cost",
          value: fmtMoney(
            recalculatedValues.iceFuelCostAnnual ||
              reportData!.iceFuelCostAnnual
          ),
        },
        {
          category: "Annual EV Energy Cost",
          value: fmtMoney(
            recalculatedValues.evEnergyCostAnnual ||
              reportData!.evEnergyCostAnnual
          ),
        },
        {
          category: "Annual ICE Maintenance",
          value: fmtMoney(
            recalculatedValues.iceMaintAnnual || reportData!.iceMaintAnnual
          ),
        },
        {
          category: "Annual EV Maintenance",
          value: fmtMoney(
            recalculatedValues.evMaintAnnual || reportData!.evMaintAnnual
          ),
        },
        {
          category: "Annual ICE Opex",
          value: fmtMoney(
            recalculatedValues.iceOpexAnnual || reportData!.iceOpexAnnual
          ),
        },
        {
          category: "Annual EV Opex",
          value: fmtMoney(
            recalculatedValues.evOpexAnnual || reportData!.evOpexAnnual
          ),
        },
        {
          category: "Annual Savings",
          value: fmtMoney(
            recalculatedValues.annualSavings || reportData!.annualSavings
          ),
        },
        {
          category: "Upfront Net Outlay",
          value: fmtMoney(
            recalculatedValues.upfrontNet || reportData!.upfrontNet
          ),
        },
      ];

      // Prepare updated environmental table
      const updatedEnvironmentalTable = [
        {
          category: "Annual Gasoline Avoided",
          value: fmtNum(
            recalculatedValues.gallonsAvoidedAnnual ||
              reportData!.gallonsAvoidedAnnual,
            0
          ),
        },
        {
          category: "Annual CO2 Offset (Tailpipe)",
          value: fmtNum(
            recalculatedValues.co2AnnualKgTailpipe ||
              reportData!.co2AnnualKgTailpipe,
            0
          ),
        },
        {
          category: "Annual Grid Emissions",
          value: fmtNum(reportData!.gridEmissionsAnnual, 0),
        },
        {
          category: "Annual Net CO2",
          value: fmtNum(
            recalculatedValues.co2AnnualKgNet || reportData!.co2AnnualKgNet,
            0
          ),
        },
        {
          category: `${analysisYears}-Year Net CO₂`,
          value: fmtNum(
            recalculatedValues.co2NetTons || reportData!.co2NetTons,
            2
          ),
        },
      ];

      // Prepare updated chart data based on recalculated values
      const updatedCostOverTimeData = {
        labels: reportData!.yearsArray.map((year) => `Year ${year}`),
        datasets: [
          {
            label: "ICE Opex",
            data: [0, ...reportData!.costOverTimeIce],
          },
          {
            label: "EV Opex",
            data: [0, ...reportData!.costOverTimeEv],
          },
        ],
      };

      const updatedCumulativeSavingsData = {
        labels: reportData!.yearsArray.map((year) => `Year ${year}`),
        data: reportData!.cumulativeNetSavings,
      };

      const updatedEnvironmentalData = {
        labels: [
          "Gasoline Avoided (gal)",
          "Tailpipe CO2 Avoided (kg)",
          "Net CO2 (kg)",
        ],
        data: [
          recalculatedValues.gallonsAvoidedAnnual ||
            reportData!.gallonsAvoidedAnnual,
          recalculatedValues.co2AnnualKgTailpipe ||
            reportData!.co2AnnualKgTailpipe,
          recalculatedValues.co2AnnualKgNet || reportData!.co2AnnualKgNet,
        ],
      };

      // Update the report using the new API
      await dispatch(
        updateFleetReportData({
          id: savedReport._id!,
          netSavings: recalculatedValues.netSavings || reportData!.netSavings,
          annualSavings:
            recalculatedValues.annualSavings || reportData!.annualSavings,
          financialTable: updatedFinancialTable,
          environmentalTable: updatedEnvironmentalTable,
          paybackPeriod:
            recalculatedValues.paybackYears || reportData!.paybackYears,
          costOverTimeData: updatedCostOverTimeData,
          cumulativeSavingsData: updatedCumulativeSavingsData,
          environmentalData: updatedEnvironmentalData,
        })
      ).unwrap();

      setIsEditing(false);
      setRecalculatedNetSavings(null);
      setRecalculatedValues({});
      setIsRecalculating(false);

      // Clear any pending timeout
      if (recalculationTimeout) {
        clearTimeout(recalculationTimeout);
        setRecalculationTimeout(null);
      }
    } catch (err) {
      console.error("Failed to save fleet report data:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedValues({ financial: {}, environmental: {} });
    setIsEditing(false);
    setRecalculatedNetSavings(null);
    setRecalculatedValues({});
    setIsRecalculating(false);

    if (recalculationTimeout) {
      clearTimeout(recalculationTimeout);
      setRecalculationTimeout(null);
    }
  };

  useEffect(() => {
    return () => {
      if (recalculationTimeout) {
        clearTimeout(recalculationTimeout);
      }
    };
  }, [recalculationTimeout]);

  const handleValueChange = (
    section: "financial" | "environmental",
    key: string,
    newValue: number
  ) => {
    const updatedValues = {
      ...editedValues,
      [section]: {
        ...editedValues[section],
        [key]: newValue,
      },
    };

    setEditedValues(updatedValues);

    // For environmental edits, we need to ensure dependent fields are recalculated
    if (section === "environmental") {
      if (key === "gallonsAvoidedAnnual") {
        const cleanedEnvironmental = { ...updatedValues.environmental };
        delete cleanedEnvironmental.co2AnnualKgTailpipe;
        delete cleanedEnvironmental.co2AnnualKgNet;
        delete cleanedEnvironmental.co2NetTons;

        triggerRecalculation(updatedValues.financial, cleanedEnvironmental);
      } else {
        triggerRecalculation(
          updatedValues.financial,
          updatedValues.environmental
        );
      }
    } else {
      triggerRecalculation(
        updatedValues.financial,
        updatedValues.environmental
      );
    }
  };

  // Map initialization
  useEffect(() => {
    let map: google.maps.Map | null = null;
    let panorama: google.maps.StreetViewPanorama | null = null;
    let marker: google.maps.Marker | null = null;
    let infoWindow: google.maps.InfoWindow | null = null;

    const initMap = () => {
      if (!mapRef.current || !window.google || !reportData?.address) return;

      const defaultPosition = new window.google.maps.LatLng(
        40.518715,
        -74.412095
      );
      const address = reportData.address || "981 US Route 1, Edison, NJ, USA";

      // Initialize the map
      map = new window.google.maps.Map(mapRef.current, {
        center: defaultPosition,
        zoom: 15,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
      });

      // Initialize Street View panorama
      panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
        position: defaultPosition,
        pov: { heading: 165, pitch: 0 },
        zoom: 1,
        visible: mapView === "streetview",
      });

      const streetViewService = new window.google.maps.StreetViewService();
      const geocoder = new window.google.maps.Geocoder();

      const updateMapView = (
        view: "streetview" | "roadmap" | "satellite",
        position: google.maps.LatLng
      ) => {
        if (!mapRef.current || !map || !panorama) return;

        if (view === "streetview") {
          mapRef.current.innerHTML = "";
          panorama = new window.google.maps.StreetViewPanorama(mapRef.current, {
            position: position,
            pov: panorama.getPov(),
            zoom: panorama.getZoom(),
            visible: true,
          });
          map.setStreetView(panorama);
        } else {
          mapRef.current.innerHTML = "";
          panorama.setVisible(false);
          map = new window.google.maps.Map(mapRef.current, {
            center: position,
            zoom: 15,
            mapTypeId:
              view === "satellite"
                ? window.google.maps.MapTypeId.SATELLITE
                : window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: false,
            streetViewControl: false,
          });
        }

        if (marker) {
          marker.setMap(null);
          marker.setPosition(position);
        } else {
          marker = new window.google.maps.Marker({
            position: position,
            title: "Fleet Location",
            icon: {
              url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              scaledSize: new window.google.maps.Size(32, 32),
            },
          });
        }

        if (infoWindow) {
          infoWindow.close();
          infoWindow.setContent(
            `<b>Location:</b> ${address}<br><b>Fleet Operation Site${
              view === "streetview"
                ? ""
                : view === "satellite"
                ? " (Satellite View)"
                : " (Map View)"
            }</b>`
          );
        } else {
          infoWindow = new window.google.maps.InfoWindow({
            content: `<b>Location:</b> ${address}<br><b>Fleet Operation Site</b>`,
          });
        }

        if (view !== "streetview") {
          marker.setMap(map);
          infoWindow.open(map, marker);
        } else {
          marker.setMap(null);
          infoWindow.close();
        }
      };

      // Geocode the address
      geocoder.geocode({ address }, (results, status) => {
        if (
          status === window.google.maps.GeocoderStatus.OK &&
          results &&
          results[0]
        ) {
          const pos = results[0].geometry.location;

          // Check Street View availability
          streetViewService.getPanorama(
            { location: pos, radius: 50 },
            (data, svStatus) => {
              const available =
                svStatus === window.google.maps.StreetViewStatus.OK;
              setStreetViewAvailable(available);
              if (!available && mapView === "streetview") {
                setMapView("roadmap");
                setSnackbarMessage(
                  "Street View is not available for this location."
                );
                setSnackbarSeverity("warning");
                setSnackbarOpen(true);
              }
              updateMapView(mapView, pos);
              setMapLoaded(true);
            }
          );
        } else {
          updateMapView(mapView, defaultPosition);
          setMapLoaded(true);
        }
      });
    };

    const loadScript = () => {
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
      script.async = true;
      script.onload = initMap;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    };

    loadScript();

    return () => {
      if (marker) marker.setMap(null);
      if (infoWindow) infoWindow.close();
      if (map) google.maps.event.clearInstanceListeners(map);
      if (panorama) google.maps.event.clearInstanceListeners(panorama);
    };
  }, [reportData?.address, mapView]);

  // Chart colors
  const ICE_RED = "#b91c1c";
  const EV_GREEN = "#16a34a";
  const BRAND_COLOR = "#182444";

  // Break-even plugin for cumulative chart
  const breakEvenPlugin = {
    id: "breakeven",
    afterDraw(chart: any) {
      const {
        ctx,
        chartArea: { left, right, top, bottom },
        scales: { x, y },
      } = chart;
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.35)";
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      try {
        const y0 = y.getPixelForValue(0);
        ctx.moveTo(left, y0);
        ctx.lineTo(right, y0);
        ctx.stroke();
      } catch (error) {
        console.warn("Error drawing break-even line:", error);
      } finally {
        ctx.restore();
      }
    },
  };

  // Chart initialization
  useEffect(() => {
    if (!reportData) return;

    Chart.register(breakEvenPlugin);

    const initializeCharts = () => {
      // Annual Cost Comparison Chart
      const costComparisonCtx =
        costComparisonChartRef.current?.getContext("2d");
      if (costComparisonCtx) {
        if (costComparisonChartInstance.current)
          costComparisonChartInstance.current.destroy();

        costComparisonChartInstance.current = new Chart(costComparisonCtx, {
          type: "bar",
          data: {
            labels: ["ICE Opex", "EV Opex"],
            datasets: [
              {
                label: "Annual Cost (USD)",
                data: [reportData.iceOpexAnnual, reportData.evOpexAnnual],
                backgroundColor: [ICE_RED, EV_GREEN],
                borderColor: [ICE_RED, EV_GREEN],
                borderWidth: 1,
                borderSkipped: false,
                barPercentage: 0.35,
                categoryPercentage: 0.6,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => `$${Number(value).toLocaleString()}`,
                },
              },
            },
          },
        });
      }

      // Cost of Gasoline vs Electric Over Time Chart
      const costOverTimeCtx = costOverTimeChartRef.current?.getContext("2d");
      if (costOverTimeCtx) {
        if (costOverTimeChartInstance.current)
          costOverTimeChartInstance.current.destroy();

        costOverTimeChartInstance.current = new Chart(costOverTimeCtx, {
          type: "line",
          data: {
            labels: reportData.yearsArray.map((year) => `Year ${year}`),
            datasets: [
              {
                label: "ICE Opex (Fuel + Maint.)",
                data: reportData.costOverTimeIce,
                borderColor: ICE_RED,
                backgroundColor: "rgba(185, 28, 28, 0.1)",
                fill: false,
                tension: 0.4,
                pointBackgroundColor: ICE_RED,
              },
              {
                label: "EV Opex (Energy + Maint.)",
                data: reportData.costOverTimeEv,
                borderColor: EV_GREEN,
                backgroundColor: "rgba(22, 163, 74, 0.1)",
                fill: false,
                tension: 0.4,
                pointBackgroundColor: EV_GREEN,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Annual Cost ($)" },
                ticks: {
                  callback: (value) => `$${Number(value).toLocaleString()}`,
                },
              },
              x: {
                title: { display: true, text: "Year" },
              },
            },
            plugins: {
              legend: {
                position: "top",
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.dataset.label || "";
                    const value = context.parsed.y;
                    return `${label}: $${Number(value).toLocaleString()}`;
                  },
                },
              },
            },
          },
        });
      }

      // Break-even Timeline (Cumulative Net Savings) Chart
      const cumulativeCtx = cumulativeChartRef.current?.getContext("2d");
      if (cumulativeCtx) {
        if (cumulativeChartInstance.current)
          cumulativeChartInstance.current.destroy();

        cumulativeChartInstance.current = new Chart(cumulativeCtx, {
          type: "line",
          data: {
            labels: reportData.yearsArray.map((year) => `Year ${year}`),
            datasets: [
              {
                label: "Cumulative Net Savings",
                data: reportData.cumulativeNetSavings,
                borderColor: BRAND_COLOR,
                backgroundColor: "rgba(24, 36, 68, 0.1)",
                fill: true,
                tension: 0.4,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                title: { display: true, text: "Cumulative Savings ($)" },
                ticks: {
                  callback: (value) => `$${Number(value).toLocaleString()}`,
                },
              },
              x: {
                title: { display: true, text: "Year" },
              },
            },
            plugins: {
              legend: {
                position: "bottom",
              },
            },
          },
          plugins: [breakEvenPlugin],
        });
      }

      // Environmental Impact (Annual) Chart
      const environmentalCtx = environmentalChartRef.current?.getContext("2d");
      if (environmentalCtx) {
        if (environmentalChartInstance.current)
          environmentalChartInstance.current.destroy();

        const environmentalData = [
          reportData.gallonsAvoidedAnnual,
          reportData.co2AnnualKgTailpipe,
          reportData.co2AnnualKgNet,
        ];

        const environmentalLabels = [
          "Annual Gasoline Avoided (gal)",
          "Tailpipe CO₂ Avoided (kg)",
          "Net CO₂ (kg)",
        ];

        environmentalChartInstance.current = new Chart(environmentalCtx, {
          type: "bar",
          data: {
            labels: environmentalLabels,
            datasets: [
              {
                label: "Environmental Impact",
                data: environmentalData,
                backgroundColor: [BRAND_COLOR, "#4b5b5d", EV_GREEN],
                borderColor: [BRAND_COLOR, "#4b5b5d", EV_GREEN],
                borderWidth: 1,
                borderSkipped: false,
                barPercentage: 0.35,
                categoryPercentage: 0.6,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Amount" },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    return `${context.label}: ${Number(
                      context.parsed.y
                    ).toLocaleString()}`;
                  },
                },
              },
            },
          },
        });
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeCharts, 100);

    return () => {
      clearTimeout(timer);
      costComparisonChartInstance.current?.destroy();
      costOverTimeChartInstance.current?.destroy();
      cumulativeChartInstance.current?.destroy();
      environmentalChartInstance.current?.destroy();
    };
  }, [reportData]);

  const handleMapViewChange = (e: any) => {
    const newView = e.target.value;
    if (newView === "streetview" && !streetViewAvailable) {
      setSnackbarMessage("Street View is not available for this location.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    setMapView(newView);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportData) {
      alert("No report data to download.");
      return;
    }

    const isWhiteLabel = downloadFormat === "WL";
    let whiteLabelConfig: any;

    if (isWhiteLabel) {
      if (!whiteLabel && !whiteLabelLoading) {
        await dispatch(getMyAddWhiteLabel());
      }

      if (
        whiteLabel?.brandLogo &&
        whiteLabel?.brandColor &&
        whiteLabel?.textColor &&
        whiteLabel?.bgColor &&
        whiteLabel?.domainURL &&
        whiteLabel?.termsOfServiceURL &&
        whiteLabel?.privacyPolicyURL
      ) {
        whiteLabelConfig = {
          brandLogo: whiteLabel.brandLogo,
          brandColor: whiteLabel.brandColor,
          textColor: whiteLabel.textColor,
          bgColor: whiteLabel.bgColor,
          domainURL: whiteLabel.domainURL || "",
          termsOfServiceURL: whiteLabel.termsOfServiceURL || "",
          privacyPolicyURL: whiteLabel.privacyPolicyURL || "",
        };
      }
    }

    try {
      setDownloading(true);
      const details: FleetReportDetails = {
        reportName: savedReport?.reportName || "Fleet EV",
        netSavings: savedReport?.netSavings ?? reportData.netSavings,
        annualSavings: reportData.annualSavings,
        address: reportData.address || "",
        city: reportData.city || "",
        zipCode: reportData.zipCode || "",
        fleetSize: parseInt(formData?.fleetSize || "1"),
        analysisYears: analysisYears,
        paybackPeriod: paybackYears,
        financialTable: savedReport?.financialTable || [
          {
            category: "Annual ICE Fuel Cost",
            value: fmtMoney(reportData.iceFuelCostAnnual),
          },
          {
            category: "Annual EV Energy Cost",
            value: fmtMoney(reportData.evEnergyCostAnnual),
          },
          {
            category: "Annual ICE Maintenance",
            value: fmtMoney(reportData.iceMaintAnnual),
          },
          {
            category: "Annual EV Maintenance",
            value: fmtMoney(reportData.evMaintAnnual),
          },
          {
            category: "Annual ICE Opex",
            value: fmtMoney(reportData.iceOpexAnnual),
          },
          {
            category: "Annual EV Opex",
            value: fmtMoney(reportData.evOpexAnnual),
          },
          {
            category: "Annual Savings",
            value: fmtMoney(reportData.annualSavings),
          },
          {
            category: "Upfront EVs Cost",
            value: fmtMoney(reportData.upfrontEVs),
          },
          {
            category: "Incentives Total",
            value: fmtMoney(reportData.incentives),
          },
          {
            category: "Resale Value Total",
            value: fmtMoney(reportData.resaleTotal),
          },
          {
            category: "Upfront Gross",
            value: fmtMoney(reportData.upfrontGross),
          },
          {
            category: "Upfront Net Outlay",
            value: fmtMoney(reportData.upfrontNet),
          },
        ],
        taxBenefitsTable:
          savedReport?.taxBenefitsTable ||
          (reportData.applyTaxBenefits
            ? [
                {
                  category: "Business Tax Rate",
                  value: `${reportData.businessTaxRate}%`,
                },
                {
                  category: "Section 179 Limit",
                  value: fmtMoney(reportData.section179Limit),
                },
                {
                  category: "Bonus Depreciation %",
                  value: `${reportData.bonusDepreciationPct}%`,
                },
                {
                  category: "Qualifying Spend",
                  value: fmtMoney(reportData.qualifyingSpend),
                },
                {
                  category: "Est. Section 179 Deduction",
                  value: fmtMoney(reportData.est179Deduction),
                },
                {
                  category: "Est. 168(k) Deduction",
                  value: fmtMoney(reportData.est168kDeduction),
                },
                {
                  category: "Estimated Tax Savings",
                  value: fmtMoney(reportData.estTaxSavings),
                },
              ]
            : undefined),
        environmentalTable: savedReport?.environmentalTable || [
          {
            category: "Annual Gasoline Avoided",
            value: fmtNum(reportData.gallonsAvoidedAnnual, 0),
          },
          {
            category: "Annual CO2 Offset (Tailpipe)",
            value: fmtNum(reportData.co2AnnualKgTailpipe, 0),
          },
          {
            category: "Annual Grid Emissions",
            value: fmtNum(reportData.gridEmissionsAnnual, 0),
          },
          {
            category: "Annual Net CO2",
            value: fmtNum(reportData.co2AnnualKgNet, 0),
          },
          {
            category: `${analysisYears}-Year Net CO2`,
            value: fmtNum(reportData.co2NetTons, 2),
          },
        ],
        costOverTimeData: savedReport?.costOverTimeData || {
          labels: reportData.yearsArray.map((year) => `Year ${year}`),
          datasets: [
            {
              label: "ICE Opex",
              data: [0, ...reportData.costOverTimeIce],
            },
            {
              label: "EV Opex",
              data: [0, ...reportData.costOverTimeEv],
            },
          ],
        },
        cumulativeSavingsData: savedReport?.cumulativeSavingsData || {
          labels: reportData.yearsArray.map((year) => `Year ${year}`),
          data: reportData.cumulativeNetSavings,
        },
        environmentalData: savedReport?.environmentalData || {
          labels: [
            "Gasoline Avoided (gal)",
            "Tailpipe CO2 Avoided (kg)",
            "Net CO2 (kg)",
          ],
          data: [
            reportData.gallonsAvoidedAnnual,
            reportData.co2AnnualKgTailpipe,
            reportData.co2AnnualKgNet,
          ],
        },
        utilityDetails: incomingState.utilityDetails || savedReport?.utilityDetails,
        energyCosts: incomingState.energyCosts || savedReport?.energyCosts,
        utilityRate: reportData.utilityRate,
        applyTaxBenefits: reportData.applyTaxBenefits,
      };

      await downloadFleetReport(details, isWhiteLabel, whiteLabelConfig, false);
    } catch (err) {
      alert("Failed to generate PDF. Please try again.");
      console.error("Download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  if (!reportData) {
    return (
      <Container>
        <Content>
          <Typography color="error" variant="h6" align="center">
            No fleet report data available. Please generate a report first.
          </Typography>
        </Content>
      </Container>
    );
  }

  const getFinancialValue = (key: string): number => {
    if (
      recalculatedValues[key as keyof typeof recalculatedValues] !== undefined
    ) {
      return recalculatedValues[
        key as keyof typeof recalculatedValues
      ] as number;
    }

    if (isEditing && editedValues.financial[key] !== undefined) {
      return editedValues.financial[key];
    }

    return (reportData as any)[key] || 0;
  };

  const getEnvironmentalValue = (key: string): number => {
    if (
      recalculatedValues[key as keyof typeof recalculatedValues] !== undefined
    ) {
      return recalculatedValues[
        key as keyof typeof recalculatedValues
      ] as number;
    }

    if (isEditing && editedValues.environmental[key] !== undefined) {
      return editedValues.environmental[key];
    }

    return (reportData as any)[key] || 0;
  };

  const paybackYears =
    savedReport?.paybackPeriod || reportData?.paybackYears || 0;
  const paybackDisplay = !isFinite(paybackYears)
    ? "No payback"
    : `${Math.floor(paybackYears)} yr ${Math.round(
        (paybackYears - Math.floor(paybackYears)) * 12
      )} mo`;

  const analysisYears =
    savedReport?.analysisYears || parseInt(formData?.years || "7");

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content>
          <HeaderContainer>
            <WelcomeText>Fleet EV Savings Estimator</WelcomeText>
            <ActionsContainer>
              {/* Edit Actions */}
              {!!savedReport && (
                <>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save Changes">
                        <IconButton
                          onClick={handleSave}
                          sx={{ color: "#4b5b5d" }}
                        >
                          {saving ? (
                            <CircularProgress size={24} />
                          ) : (
                            <SaveIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          onClick={handleCancel}
                          sx={{ color: "#f54242" }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Edit Report Details">
                      <IconButton
                        onClick={startEditing}
                        sx={{ color: "#4b5b5d" }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}

              <FormControl sx={{ minWidth: 50 }} size="small">
                <InputLabel id="download-format-label">Format</InputLabel>
                <Select
                  labelId="download-format-label"
                  value={downloadFormat}
                  label="Format"
                  onChange={(e) =>
                    setDownloadFormat(e.target.value as "PDF" | "WL")
                  }
                >
                  <MenuItem value="PDF">PDF</MenuItem>
                  {!isStarterPlan && <MenuItem value="WL">WL</MenuItem>}
                </Select>
              </FormControl>
              <Tooltip title="Download Report">
                <StyledButton
                  onClick={handleDownloadReport}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <GetApp />
                    )
                  }
                  disabled={downloading || whiteLabelLoading}
                >
                  {downloading ? "Downloading..." : "Download"}
                </StyledButton>
              </Tooltip>
              <Tooltip title="Back to Fleet Estimator">
                <StyledBackButton aria-label="back" onClick={handleBack}>
                  <ArrowBack />
                </StyledBackButton>
              </Tooltip>
            </ActionsContainer>
          </HeaderContainer>
          {/* Hero Section */}
          <HeroSection>
            <HeroLeft>
              <HeroLabel>
                Estimated Annual Savings (Fuel + Maintenance)
              </HeroLabel>
              <HeroValue
                className={
                  (recalculatedValues.annualSavings ??
                    reportData.annualSavings) >= 0
                    ? "positive"
                    : "negative"
                }
              >
                {fmtMoney(
                  recalculatedValues.annualSavings ?? reportData.annualSavings
                )}
              </HeroValue>
              <HeroNote>
                ICE Opex {fmtMoney(reportData.iceOpexAnnual)} vs EV Opex{" "}
                {fmtMoney(reportData.evOpexAnnual)}
              </HeroNote>
            </HeroLeft>
            <HeroRight>
              <MiniCard>
                <MiniLabel>{analysisYears}-Year Net Savings</MiniLabel>
                <MiniValue
                  className={
                    (recalculatedValues.netSavings ?? reportData.netSavings) >=
                    0
                      ? "positive"
                      : "negative"
                  }
                >
                  {fmtMoney(
                    recalculatedValues.netSavings ?? reportData.netSavings
                  )}
                </MiniValue>
              </MiniCard>
              <MiniCard>
                <MiniLabel>Upfront Net Outlay</MiniLabel>
                <MiniValue>{fmtMoney(reportData.upfrontNet)}</MiniValue>
              </MiniCard>
              <MiniCard>
                <MiniLabel>Payback Period</MiniLabel>
                <MiniValue>{paybackDisplay}</MiniValue>
              </MiniCard>
            </HeroRight>
          </HeroSection>
          {/* Financial KPIs */}
          <Card sx={{ marginBottom: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Title>Financial Overview</Title>
            </Box>
            <KpiGrid>
              <EditableKpiCard
                label="Annual ICE Fuel Cost"
                value={getFinancialValue("iceFuelCostAnnual")}
                smallText="per fleet"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "iceFuelCostAnnual", newValue)
                }
                valueType="currency"
              />
              <EditableKpiCard
                label="Annual EV Energy Cost"
                value={getFinancialValue("evEnergyCostAnnual")}
                smallText="per fleet"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "evEnergyCostAnnual", newValue)
                }
                valueType="currency"
              />
              <EditableKpiCard
                label="Annual Fuel + Maint. Savings"
                value={getFinancialValue("annualSavings")}
                smallText={`ICE Opex ${fmtMoney(
                  reportData!.iceOpexAnnual
                )} vs EV Opex ${fmtMoney(reportData!.evOpexAnnual)}`}
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "annualSavings", newValue)
                }
                valueType="currency"
                className={
                  reportData!.annualSavings >= 0 ? "positive" : "negative"
                }
                readOnly={true}
              />
              <EditableKpiCard
                label="Upfront Net Outlay (After Tax)"
                value={getFinancialValue("upfrontNet")}
                smallText="EVs - incentives - resale + infrastructure"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "upfrontNet", newValue)
                }
                valueType="currency"
                readOnly={true}
              />
            </KpiGrid>

            <KpiGrid>
              <EditableKpiCard
                label={`${analysisYears}-Year Net Savings`}
                value={getFinancialValue("netSavings")}
                smallText="Total savings - upfront"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "netSavings", newValue)
                }
                valueType="currency"
                className={
                  reportData!.netSavings >= 0 ? "positive" : "negative"
                }
                readOnly={true}
              />
              <KpiCard>
                <KpiLabel>Payback Period</KpiLabel>
                <KpiValue>{paybackDisplay}</KpiValue>
                <KpiSmall>
                  {!isFinite(paybackYears)
                    ? "No payback (annual savings ≤ 0)"
                    : "Time to recover upfront investment"}
                </KpiSmall>
              </KpiCard>
              <EditableKpiCard
                label="Annual ICE Opex"
                value={getFinancialValue("iceOpexAnnual")}
                smallText="Fuel + maintenance"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "iceOpexAnnual", newValue)
                }
                valueType="currency"
                readOnly={true}
              />
              <EditableKpiCard
                label="Annual EV Opex"
                value={getFinancialValue("evOpexAnnual")}
                smallText="Energy + maintenance"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("financial", "evOpexAnnual", newValue)
                }
                valueType="currency"
                readOnly={true}
              />
            </KpiGrid>
          </Card>
          {/* Environmental KPIs */}
          <Card sx={{ marginBottom: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Title>Environmental Impact</Title>
            </Box>
            <KpiGrid>
              <EditableKpiCard
                label="Annual Gasoline Avoided (gal)"
                value={getEnvironmentalValue("gallonsAvoidedAnnual")}
                smallText="Miles ÷ ICE MPG × fleet"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange(
                    "environmental",
                    "gallonsAvoidedAnnual",
                    newValue
                  )
                }
                valueType="number"
              />
              <EditableKpiCard
                label="Annual CO₂ Offset (kg, tailpipe)"
                value={getEnvironmentalValue("co2AnnualKgTailpipe")}
                smallText="Gallons × 8.887 kg/gal"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange(
                    "environmental",
                    "co2AnnualKgTailpipe",
                    newValue
                  )
                }
                valueType="number"
                readOnly={true}
              />
              <EditableKpiCard
                label="Annual Net CO₂ (kg)"
                value={getEnvironmentalValue("co2AnnualKgNet")}
                smallText="Tailpipe avoided − grid emissions"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("environmental", "co2AnnualKgNet", newValue)
                }
                valueType="number"
                readOnly={true}
              />
              <EditableKpiCard
                label={`${analysisYears}-Yr Net CO₂ (t)`}
                value={getEnvironmentalValue("co2NetTons")}
                smallText="Annual net × years ÷ 1,000"
                isEditing={isEditing}
                onValueChange={(newValue) =>
                  handleValueChange("environmental", "co2NetTons", newValue)
                }
                valueType="number"
                readOnly={true}
              />
            </KpiGrid>
          </Card>
          {/* Location Section */}
          <Card sx={{ marginBottom: 3 }}>
            <Title>Location Details</Title>
            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Address
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {reportData.address}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  City
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {reportData.city}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Zip Code
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {reportData.zipCode}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel id="map-view-label">Map View</InputLabel>
                <Select
                  labelId="map-view-label"
                  value={mapView}
                  label="Map View"
                  onChange={handleMapViewChange}
                >
                  <MenuItem value="streetview">Street View</MenuItem>
                  <MenuItem value="roadmap">Map</MenuItem>
                  <MenuItem value="satellite">Satellite</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <MapContainer ref={mapRef} />
            {!mapLoaded && (
              <Box sx={{ textAlign: "center", mt: 1, color: "#666" }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading map...
              </Box>
            )}
          </Card>

          {/* Utility Details Section - Only show if utility info is NOT disabled */}
          {(incomingState.utilityDetails || savedReport?.utilityDetails) && (
            <Card sx={{ marginBottom: 3 }}>
              <Title>Utility Information</Title>

              {/* Display utility details in a table format */}
              <Box sx={{ overflowX: "auto", mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow
                      style={{
                        background:
                          "linear-gradient(to left, #f4efe6, #bcc4c4)",
                      }}
                    >
                      <StyledTableCell>Field</StyledTableCell>
                      <StyledTableCell>Value</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Utility Name */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          Utility Name
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1">
                          {incomingState.utilityDetails?.utilityName ||
                            savedReport?.utilityDetails?.utilityName ||
                            "N/A"}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>

                    {/* State */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          State
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1">
                          {incomingState.utilityDetails?.state ||
                            savedReport?.utilityDetails?.state ||
                            "N/A"}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>

                    {/* Zip Code */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          Zip Code
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1">
                          {incomingState.utilityDetails?.zipCode ||
                            savedReport?.utilityDetails?.zipCode ||
                            reportData?.zipCode ||
                            "N/A"}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>

                    {/* Electricity Rate */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          Electricity Rate
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1" fontWeight={600}>
                          ${(reportData?.utilityRate || 0).toFixed(3)}/kWh
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>

                    {/* Utility ID */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          Utility ID
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1">
                          {incomingState.utilityDetails?.utilityId ||
                            savedReport?.utilityDetails?.utilityId ||
                            "N/A"}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>

                    {/* Schedule ID */}
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography variant="body2" fontWeight={600}>
                          Schedule ID
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography variant="body1">
                          {incomingState.utilityDetails?.scheduleId ||
                            savedReport?.utilityDetails?.scheduleId ||
                            "N/A"}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </Box>
            </Card>
          )}

          {hasEnergyCosts && (
            <Card sx={{ marginBottom: 3 }}>
              <Title>Energy Costs</Title>
              <Box
                sx={{
                  overflowX: "auto",
                  mt: hasUtilityDetails ? 2 : 0,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      style={{
                        background:
                          "linear-gradient(to left, #f4efe6, #bcc4c4)",
                      }}
                    >
                      <StyledTableCell>Category</StyledTableCell>
                      <StyledTableCell>Year 1</StyledTableCell>
                      <StyledTableCell>Year 2</StyledTableCell>
                      <StyledTableCell>Year 3</StyledTableCell>
                      <StyledTableCell>Year 4</StyledTableCell>
                      <StyledTableCell>Year 5</StyledTableCell>
                      <StyledTableCell>Year 6</StyledTableCell>
                      <StyledTableCell>Year 7</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <StyledTableRow>
                      <StyledTableDataCell>Energy</StyledTableDataCell>
                      {(energyCostsView?.energy ?? []).map((cost, index) => (
                        <StyledTableDataCell key={`energy-${index}`}>
                          $
                          {cost.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </StyledTableDataCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>Energy Time</StyledTableDataCell>
                      {(energyCostsView?.energyTime ?? []).map(
                        (cost, index) => (
                          <StyledTableDataCell key={`energyTime-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        )
                      )}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Incremental Energy
                      </StyledTableDataCell>
                      {(energyCostsView?.incrementalEnergy ?? []).map(
                        (cost, index) => (
                          <StyledTableDataCell key={`incEnergy-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        )
                      )}
                    </StyledTableRow>
                    {shouldShowUtilitySection && (
                      <StyledTableRow>
                        <StyledTableDataCell>Demand</StyledTableDataCell>
                        {(energyCostsView?.demand ?? []).map((cost, index) => (
                          <StyledTableDataCell key={`demand-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        ))}
                      </StyledTableRow>
                    )}
                    <StyledTableRow>
                      <StyledTableDataCell>Demand Time</StyledTableDataCell>
                      {(energyCostsView?.demandTime ?? []).map(
                        (cost, index) => (
                          <StyledTableDataCell key={`demandTime-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        )
                      )}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Incremental Demand
                      </StyledTableDataCell>
                      {(energyCostsView?.incrementalDemand ?? []).map(
                        (cost, index) => (
                          <StyledTableDataCell key={`incDemand-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        )
                      )}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>Service Charge</StyledTableDataCell>
                      {(energyCostsView?.serviceCharge ?? []).map(
                        (cost, index) => {
                          const displayCost = cost > 500 ? 500 : cost;
                          return (
                            <StyledTableDataCell key={`service-${index}`}>
                              $
                              {displayCost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              {cost > 500 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#ff6b35",
                                    ml: 0.5,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  *
                                </Typography>
                              )}
                            </StyledTableDataCell>
                          );
                        }
                      )}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>Other Charges</StyledTableDataCell>
                      {(energyCostsView?.otherCharges ?? []).map(
                        (cost, index) => (
                          <StyledTableDataCell key={`other-${index}`}>
                            $
                            {cost.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </StyledTableDataCell>
                        )
                      )}
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>Total</StyledTableDataCell>
                      {(energyCostsView?.total ?? []).map((_, index) => {
                        const energy = energyCostsView?.energy?.[index] || 0;
                        const energyTime =
                          energyCostsView?.energyTime?.[index] || 0;
                        const incrementalEnergy =
                          energyCostsView?.incrementalEnergy?.[index] || 0;
                        const demand = energyCostsView?.demand?.[index] || 0;
                        const demandTime =
                          energyCostsView?.demandTime?.[index] || 0;
                        const incrementalDemand =
                          energyCostsView?.incrementalDemand?.[index] || 0;
                        const serviceCharge =
                          energyCostsView?.serviceCharge?.[index] || 0;
                        const otherCharges =
                          energyCostsView?.otherCharges?.[index] || 0;

                        // Apply cap to service charge
                        const cappedServiceCharge =
                          serviceCharge > 500 ? 500 : serviceCharge;

                        // Conditionally include demand based on shouldShowUtilitySection
                        const demandValue = shouldShowUtilitySection
                          ? demand
                          : 0;

                        // Sum all components with capped service charge and conditional demand
                        const recalculatedTotal =
                          energy +
                          energyTime +
                          incrementalEnergy +
                          demandValue + // Use conditional demand value
                          demandTime +
                          incrementalDemand +
                          cappedServiceCharge +
                          otherCharges;

                        return (
                          <StyledTableDataCell key={`total-${index}`}>
                            $
                            {recalculatedTotal.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            {serviceCharge > 500 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#ff6b35",
                                  ml: 0.5,
                                  fontSize: "0.6rem",
                                }}
                              >
                                *
                              </Typography>
                            )}
                          </StyledTableDataCell>
                        );
                      })}
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </Box>
            </Card>
          )}

          {/* Tax Benefits Section */}
          {reportData.applyTaxBenefits && (
            <TaxBenefitsCard>
              <Title>Tax Benefits Details</Title>
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow
                      style={{
                        background:
                          "linear-gradient(to left, #f4efe6, #bcc4c4)",
                      }}
                    >
                      <StyledTableCell>Category</StyledTableCell>
                      <StyledTableCell>Value</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Business Tax Rate
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        {reportData.businessTaxRate}%
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Section 179 Limit
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        ${reportData.section179Limit.toLocaleString()}
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Bonus Depreciation %
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        {reportData.bonusDepreciationPct}%
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Qualifying Spend
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        ${reportData.qualifyingSpend.toLocaleString()}
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Estimated Section 179 Deduction
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        ${reportData.est179Deduction.toLocaleString()}
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Estimated 168(k) Deduction
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        ${reportData.est168kDeduction.toLocaleString()}
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#2e7d32" }}
                        >
                          Estimated Tax Savings
                        </Typography>
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#2e7d32" }}
                        >
                          ${reportData.estTaxSavings.toLocaleString()}
                        </Typography>
                      </StyledTableDataCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableDataCell>
                        Upfront Cost After Tax
                      </StyledTableDataCell>
                      <StyledTableDataCell>
                        ${reportData.upfrontAfterTax.toLocaleString()}
                      </StyledTableDataCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tax Disclaimer */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#fff3cd",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#856404", fontStyle: "normal" }}
                >
                  <strong>Tax disclaimer:</strong> Estimates are illustrative
                  only and do not constitute tax advice. Section 179 and 168(k)
                  eligibility and limits vary by entity and state. Consult a
                  qualified tax professional to confirm treatment for your
                  situation.
                </Typography>
              </Box>
            </TaxBenefitsCard>
          )}
          {/* Charts Section */}
          <ChartsGrid>
            <Card>
              <Title>Cost of Gasoline vs Electric Over Time</Title>
              <ChartContainer>
                <canvas ref={costOverTimeChartRef} />
              </ChartContainer>
              <SmallText>
                Annual Opex with U.S. average yearly price growth (gas ~3.9%/yr;
                electricity ~2.5%/yr).
              </SmallText>
            </Card>

            <Card>
              <Title>Annual Cost Comparison (Current Year)</Title>
              <ChartContainer>
                <canvas ref={costComparisonChartRef} />
              </ChartContainer>
            </Card>

            <Card>
              <Title>Break-even Timeline (Cumulative Net Savings)</Title>
              <ChartContainer>
                <canvas ref={cumulativeChartRef} />
              </ChartContainer>
            </Card>

            <Card>
              <Title>Environmental Impact (Annual)</Title>
              <ChartContainer>
                <canvas ref={environmentalChartRef} />
              </ChartContainer>
              <SmallText>
                Showing annual environmental impact including gasoline avoided,
                tailpipe CO₂ reduction, and net CO₂ impact considering grid
                emissions.
              </SmallText>
            </Card>
          </ChartsGrid>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Content>
      </motion.div>
    </Container>
  );
};

export default ViewFleetReport;
